import React, { useState } from "react";
import { VariationItem } from "../../../@types/inventory";
import { AddSquareIcon } from "../../../assets/icons";
import { SubtractSquareIcon } from "../../../assets/icons/SubtractSquareIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import { DropdownComponent, InputComponent } from "../../core/Forms";
import { useTranslation } from "react-i18next";
import { addCommasToNumber } from "../../../utils/convertToFloat";

interface EditVariationFormProps {
  onAddVariation: (newVariation: VariationItem) => void;
  variations: VariationItem[];
  onRemoveVariation: (id: number) => void;
  onUpdateVariation: (id: number, field: string, value: string) => void;
  variationList: any;
  variationError?: any;
}

function EditVariationForm({
  onAddVariation,
  variations,
  onRemoveVariation,
  onUpdateVariation,
  variationList,
  variationError,
}: EditVariationFormProps) {
  const handleAddVariation = () => {
    const newVariation: VariationItem = {
      name: "",
      type: "",
      costPrice: "",
      price: "",
      quantity: "",
    };
    onAddVariation(newVariation);
  };
  const { t } = useTranslation("inventories");
  const [selectedType, setSelectedType] = React.useState("");
  const [selectedName, setSelectedName] = React.useState("");
  const [focusedField, setFocusedField] = React.useState<{
    index: number;
    field: string;
  } | null>(null);

  const handleVariation = (index: number, name: string, value: string) => {
    if (name === "name") {
      setSelectedName(value);
      onUpdateVariation(index, "name", value);
    }
    if (name === "type") {
      setSelectedType(value);
      onUpdateVariation(index, "type", value);
    }
  };

  const variationNameOptions = variationList.map((item: any) => ({
    value: item.variantName,
    label: item.variantName,
  }));

  const variant = variationList.find(
    (item: any) => item.variantName === selectedName,
  );

  const variationTypesOptions =
    variant?.types.map((type: string) => ({ value: type, label: type })) || [];

  const formatCurrency = (value: string | number | undefined) => {
    if (value === undefined || value === null) return "";

    // Convert to string if it's a number
    const strValue = typeof value === "number" ? value.toString() : value;

    // Remove all non-digit characters except decimal point
    const numStr = strValue.replace(/[^\d.]/g, "");
    const num = parseFloat(numStr || "0");
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const parseCurrencyInput = (value: string) => {
    // Remove all formatting (commas) but keep numbers and decimal point
    return value.replace(/[^\d.]/g, "");
  };

  const handlePriceChange = (index: number, field: string, value: string) => {
    const parsedValue = parseCurrencyInput(value);
    onUpdateVariation(index, field, parsedValue);
  };

  const getDisplayValue = (
    index: number,
    field: string,
    value: string | number | undefined,
  ) => {
    if (focusedField?.index === index && focusedField?.field === field) {
      return value === undefined || value === null ? "" : String(value); // Show raw value when focused
    }
    if (field === "costPrice" || field === "price") {
      return formatCurrency(value);
    }
    return value === undefined || value === null ? "" : String(value);
  };

  return (
    <div className="">
      {variations.length > 0 && (
        <div className="hidden lg:block text-[16px] font-semibold text-[#212121]">
          {" "}
          {t("inventory.productVariation")}
        </div>
      )}
      <div className="w-full mt-6">
        <div className="lg:hidden mb-4 lg:px-0">
          <p className="text-[18px] md:text-[20px] font-medium">
            {" "}
            {t("inventory.productVariation")}
          </p>
        </div>
        <div className="md:block hidden">
          {Array.isArray(variations) && variations.length > 0 && (
            <div className="mt-4">
              <table className="w-full border-collapse border border-gray-200">
                <thead className="bg-[#F9FAFB]">
                  <tr>
                    <th className="border border-gray-200 px-4 py-2">Type</th>
                    <th className="border border-gray-200 px-4 py-2">
                      Cost Price
                    </th>
                    <th className="border border-gray-200 px-4 py-2">
                      Selling Price
                    </th>
                    <th className="border border-gray-200 px-4 py-2">
                      Quantity
                    </th>
                    <th className="border border-gray-200 px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {variations.map((variant, index) => (
                    <tr key={`${variant.id}-`}>
                      <td className="border capitalize border-gray-200 px-4 py-2 font-semibold">
                        {variant.type}
                      </td>

                      <td className="border border-gray-200 px-4 py-2">
                        <input
                          placeholder="Cost Price"
                          required
                          type="text"
                          value={addCommasToNumber(variant.costPrice ?? 0)}
                          onChange={(e) =>
                            handlePriceChange(
                              index,
                              "costPrice",
                              e.target.value,
                            )
                          }
                          className={`border border-gray-200 px-2 py-1 rounded w-full `}
                        />
                      </td>

                      <td className={`border border-gray-200 px-4 py-2 `}>
                        <input
                          placeholder="Selling Price"
                          required
                          type="text"
                          value={addCommasToNumber(variant.price ?? "")}
                          onChange={(e) =>
                            handlePriceChange(index, "price", e.target.value)
                          }
                          className={`border border-gray-200 px-2 py-1 rounded w-full `}
                        />
                      </td>

                      <td className="border border-gray-200 px-4 py-2">
                        <input
                          type="number"
                          placeholder="Quantity"
                          required
                          value={variant.quantity || ""}
                          onChange={(e) =>
                            onUpdateVariation(index, "quantity", e.target.value)
                          }
                          className={`border border-gray-200 px-2 py-1 rounded w-full `}
                        />
                      </td>
                      <td className="border border-gray-200 px-4 py-2">
                        <button
                          onClick={() => onRemoveVariation(index)}
                          className="p-2 "
                        >
                          <DeleteIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="block md:hidden mb-4">
          <div className="space-y-3">
            {variations.map((variant, index) => (
              <div
                key={index}
                className="border border-gray-200 bg-[#FCFCFD] p-3 rounded-lg w-full"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 mb-4 font-semibold text-[14px] text-gray-700">
                    <p className="leading-5">{variant.name}</p>
                    <div className="w-1 h-1 bg-[#D0D5DD] rounded-full" />
                    <p className="leading-5">{variant.type}</p>
                  </div>
                  <div
                    onClick={() => onRemoveVariation(index)}
                    className="text-red-500 mb-4 font-semibold text-[14px] cursor-pointer"
                  >
                    Remove
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex w-full gap-4">
                    <div>
                      <InputComponent
                        placeholder="Cost price"
                        className="w-full"
                        type="text"
                        value={
                          variant.costPrice
                            ? addCommasToNumber(variant.costPrice ?? 0)
                            : ""
                        }
                        //@ts-ignore
                        handleChange={(e: { target: { value: any } }) =>
                          handlePriceChange(index, "costPrice", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <InputComponent
                        placeholder="Selling price"
                        className="w-full"
                        type="text"
                        value={
                          variant.price
                            ? addCommasToNumber(variant.price ?? 0)
                            : ""
                        }
                        //@ts-ignore
                        handleChange={(e: { target: { value: any } }) =>
                          handlePriceChange(index, "price", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <InputComponent
                    type="number"
                    placeholder="Quantity"
                    className="w-full"
                    value={variant.quantity || ""}
                    handleChange={(e: { target: { value: any } }) =>
                      onUpdateVariation(index, "quantity", e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export { EditVariationForm };
